/**
 * EMICalculator — Indian-finance compliant, two-mode (Reducing vs Flat) interactive.
 *
 * Behaviour:
 *   • Two SEPARATE interest-rate sliders, each driving its own card.
 *   • Moving one slider zeroes the other (mutually exclusive simulation).
 *   • If both are zero → both cards show 0 / interest-free purchase.
 *   • Tenure slider: 6 → 144 months (12 years), step 6.
 *   • Down-payment slider: 0% → 90%.
 *
 * Math:
 *   Reducing-Balance (Banks): EMI = P·r·(1+r)^n / ((1+r)^n − 1)
 *   Flat-Rate (NBFC):
 *     SimpleInterest = P × R × T (years)
 *     Monthly EMI    = (P + SimpleInterest) / n
 */
import { useMemo, useState } from "react";
import { Calculator, Wallet, Percent, Clock, IndianRupee, TrendingUp } from "lucide-react";
import { formatINR } from "@/lib/api";

export default function EMICalculator({ defaultPrice = 0, productTitle = "" }) {
  const [price, setPrice] = useState(defaultPrice || 1500000);
  const [downPct, setDownPct] = useState(20);
  const [months, setMonths] = useState(36);
  const [reducingRate, setReducingRate] = useState(11);
  const [flatRate, setFlatRate] = useState(0);

  const principal = Math.max(0, price - (price * downPct) / 100);
  const years = months / 12;

  // Mutually-exclusive sliders — moving one zeros the other.
  const onReducingChange = (v) => { setReducingRate(v); if (v > 0) setFlatRate(0); };
  const onFlatChange = (v) => { setFlatRate(v); if (v > 0) setReducingRate(0); };

  const reducing = useMemo(() => {
    const r = reducingRate / 100 / 12;
    let emi = 0;
    if (principal && months) emi = r === 0 ? principal / months : (principal * r * Math.pow(1 + r, months)) / (Math.pow(1 + r, months) - 1);
    const total = emi * months;
    return { emi, total, interest: total - principal };
  }, [principal, months, reducingRate]);

  const flat = useMemo(() => {
    const interest = (principal * flatRate * years) / 100;
    const total = principal + interest;
    const emi = months ? total / months : 0;
    return { emi, total, interest };
  }, [principal, months, years, flatRate]);

  return (
    <section className="bg-gradient-to-br from-[#FAFAFA] via-white to-gray-100 border-y-2 border-[#1A1A1A] py-16 relative overflow-hidden" data-testid="emi-calculator">
      {/* Subtle decorative grid */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{ backgroundImage: "linear-gradient(#1A1A1A 1px, transparent 1px), linear-gradient(90deg, #1A1A1A 1px, transparent 1px)", backgroundSize: "40px 40px" }}
        aria-hidden
      />
      <div className="absolute -top-20 -right-20 w-80 h-80 bg-[#FFCC00] rounded-full blur-3xl opacity-20 pointer-events-none" aria-hidden />
      <div className="absolute -bottom-32 -left-20 w-96 h-96 bg-[#E65C00] rounded-full blur-3xl opacity-10 pointer-events-none" aria-hidden />

      <div className="relative max-w-6xl mx-auto px-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-[#1A1A1A] text-[#FFCC00] flex items-center justify-center">
            <Calculator className="w-5 h-5" />
          </div>
          <div className="label-eyebrow">Finance Planner</div>
        </div>
        <h2 className="heading-xl text-3xl sm:text-4xl lg:text-5xl">
          PLAN YOUR <span className="text-[#E65C00]">EMI</span>.
        </h2>
        <p className="mt-3 text-sm sm:text-base text-gray-700 max-w-2xl">
          Estimate your monthly outflow for {productTitle ? <strong className="text-[#1A1A1A]">{productTitle}</strong> : "this machine"}. Move either the <strong>Reducing</strong> or <strong>Flat</strong> rate slider — moving one resets the other so you can compare a bank quote against an NBFC quote independently.
        </p>

        <div className="grid lg:grid-cols-[1.05fr_1fr] gap-6 mt-8">
          {/* ── INPUTS ── */}
          <div className="bg-white border border-gray-200 shadow-sm p-6 sm:p-7 space-y-6" data-testid="emi-inputs">
            <NumberSlider
              icon={IndianRupee}
              label="On-Road Price (₹)"
              value={price} min={50000} max={50000000} step={5000}
              onChange={setPrice}
              displaySuffix="INR"
              helper={`Displayed: ${formatINR(price)} · drive-away cost (machine + GST + insurance + RTO). Step: ₹5,000.`}
              testid="emi-price"
            />
            <NumberSlider
              icon={Wallet}
              label="Down Payment (%)"
              value={downPct} min={0} max={90} step={1}
              onChange={setDownPct}
              displaySuffix="%"
              helper={`That's ${formatINR((price * downPct) / 100)} upfront. Higher down payment → smaller loan & lower EMI.`}
              testid="emi-down"
            />
            <NumberSlider
              icon={Clock}
              label="Loan Tenure (months)"
              value={months} min={6} max={144} step={6}
              onChange={setMonths}
              displaySuffix="mo"
              helper={`${(months / 12).toFixed(1)} years. Standard commercial-vehicle loans run 12 – 84 months. Up to 144 months (12 yrs) for select profiles.`}
              testid="emi-tenure"
            />

            {/* Two interest-rate sliders, mutually exclusive */}
            <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-dashed border-gray-200">
              <RateSlider
                label="Reducing-Balance"
                sublabel="(Banks)"
                accent="#FFCC00"
                value={reducingRate}
                onChange={onReducingChange}
                disabled={flatRate > 0}
                testid="emi-rate-reducing"
              />
              <RateSlider
                label="Flat Rate"
                sublabel="(NBFC commercial finance)"
                accent="#E65C00"
                value={flatRate}
                onChange={onFlatChange}
                disabled={reducingRate > 0}
                testid="emi-rate-flat"
              />
            </div>

            <div className="bg-[#FFCC00]/15 border-l-4 border-[#E65C00] p-3 text-[11px] text-gray-700 leading-relaxed">
              <strong className="text-[#1A1A1A] uppercase tracking-wider text-[10px]">Tip:</strong>{" "}
              Move the slider on the rate model your lender quotes — the other model auto-zeros. Use this to verify whether a bank's "lower-looking" reducing rate actually costs less than an NBFC's flat rate over the full tenure.
            </div>
          </div>

          {/* ── RESULTS ── */}
          <div className="space-y-4">
            <ResultCard
              variant="reducing"
              title="Reducing-Balance"
              subtitle="Standard banking EMI"
              ratePct={reducingRate}
              principal={principal}
              months={months}
              {...reducing}
            />
            <ResultCard
              variant="flat"
              title="Flat Rate"
              subtitle="NBFC commercial-vehicle finance"
              ratePct={flatRate}
              principal={principal}
              months={months}
              {...flat}
            />
          </div>
        </div>

        {/* Narrative — informative, no specific rate band */}
        <div className="mt-8 grid md:grid-cols-2 gap-4">
          <div className="bg-white border border-gray-200 p-5">
            <div className="flex items-center gap-2 mb-2">
              <Percent className="w-4 h-4 text-[#FFCC00]" />
              <div className="text-xs uppercase tracking-widest font-black text-[#1A1A1A]">How Reducing-Balance is Calculated</div>
            </div>
            <p className="text-xs text-gray-700 leading-relaxed">
              Banks compute interest <strong>only on the outstanding principal</strong> at the start of each month. As you pay EMIs, the principal shrinks, so the interest portion of each EMI shrinks too. The standard EMI formula: <code className="bg-gray-100 px-1.5 py-0.5 text-[10px] font-mono">EMI = P × r × (1+r)<sup>n</sup> / ((1+r)<sup>n</sup> − 1)</code>, where P is the loan, r is the monthly rate, and n is the number of months. This is what RBI mandates for all bank loans — and why a "11% reducing" loan is genuinely cheaper than an "11% flat" loan.
            </p>
          </div>
          <div className="bg-white border border-gray-200 p-5">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-[#E65C00]" />
              <div className="text-xs uppercase tracking-widest font-black text-[#1A1A1A]">How Flat Rate is Calculated</div>
            </div>
            <p className="text-xs text-gray-700 leading-relaxed">
              Most commercial-vehicle &amp; equipment NBFCs quote a <strong>flat rate</strong>: interest is computed on the <em>original</em> principal for the <em>entire</em> tenure, then added upfront and divided into equal EMIs. Formula: <code className="bg-gray-100 px-1.5 py-0.5 text-[10px] font-mono">Interest = P × R × T(yrs); EMI = (P + Interest) / n</code>. The headline number looks small, but the effective reducing-equivalent is roughly <strong>1.85× to 2×</strong> the flat rate — always compare both before signing.
            </p>
          </div>
        </div>

        <div className="mt-6 text-[11px] text-gray-500 leading-relaxed text-center">
          Indicative calculator only. Final rate depends on your CIN/CIBIL profile, equipment age, repayment history and lender policy on the day. Tara Autohub partners with 8+ NBFCs &amp; banks — pre-approval in 48 hrs.
        </div>
      </div>
    </section>
  );
}

/* ─────────────── Sub-components ─────────────── */

function NumberSlider({ icon: Icon, label, value, min, max, step, onChange, displaySuffix = "", helper = "", testid }) {
  const onTextChange = (e) => {
    const raw = e.target.value;
    if (raw === "" || raw === "-") { onChange(min); return; }
    let n = Number(raw);
    if (Number.isNaN(n)) return;
    if (n < min) n = min;
    if (n > max) n = max;
    onChange(n);
  };
  return (
    <div>
      <div className="flex items-end justify-between mb-2 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-[#E65C00]" />}
          <label className="text-[10px] uppercase tracking-[0.2em] font-black text-gray-600">{label}</label>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="number"
            inputMode="numeric"
            min={min} max={max} step={step}
            value={value}
            onChange={onTextChange}
            className="w-28 sm:w-32 border-2 border-gray-200 focus:border-[#E65C00] focus:outline-none font-heading font-black text-sm text-right pr-2 py-1.5"
            data-testid={`${testid}-input`}
            aria-label={`${label} value`}
          />
          {displaySuffix && <span className="text-xs uppercase tracking-widest font-bold text-gray-500">{displaySuffix}</span>}
        </div>
      </div>
      <input
        type="range"
        min={min} max={max} step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[#E65C00] cursor-pointer"
        data-testid={testid}
      />
      {helper && <div className="text-[10px] text-gray-500 mt-1.5 leading-relaxed">{helper}</div>}
    </div>
  );
}

function Slider({ icon: Icon, label, value, min, max, step, onChange, display, hint, testid }) {
  return (
    <div>
      <div className="flex items-end justify-between mb-2">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-[#E65C00]" />}
          <label className="text-[10px] uppercase tracking-[0.2em] font-black text-gray-600">{label}</label>
        </div>
        <div className="text-base font-heading font-black text-[#1A1A1A]">{display}</div>
      </div>
      <input
        type="range"
        min={min} max={max} step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[#E65C00] cursor-pointer"
        data-testid={testid}
      />
      {hint && <div className="text-[10px] text-gray-500 mt-1.5 leading-relaxed">{hint}</div>}
    </div>
  );
}

function RateSlider({ label, sublabel, accent, value, onChange, disabled, testid }) {
  const onTextChange = (e) => {
    const raw = e.target.value;
    if (raw === "" || raw === "-") { onChange(0); return; }
    let n = Number(raw);
    if (Number.isNaN(n)) return;
    if (n < 0) n = 0;
    if (n > 30) n = 30;
    // round to 0.1 precision so it stays in sync with the slider
    n = Math.round(n * 10) / 10;
    onChange(n);
  };
  return (
    <div className={`p-3 border-2 transition-all ${value > 0 ? "" : "opacity-60"}`} style={{ borderColor: value > 0 ? accent : "#E5E7EB", background: value > 0 ? `${accent}1a` : "#fff" }}>
      <div className="flex items-center justify-between mb-1.5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-black text-[#1A1A1A]">{label}</div>
          <div className="text-[9px] text-gray-500">{sublabel}</div>
        </div>
        <div className="flex items-center gap-1">
          <input
            type="number"
            inputMode="decimal"
            min={0} max={30} step={0.1}
            value={value}
            onChange={onTextChange}
            disabled={disabled}
            className="w-16 border-2 border-gray-200 focus:border-[#E65C00] focus:outline-none font-heading font-black text-base text-right pr-1 py-1 disabled:bg-gray-50 disabled:text-gray-400"
            style={{ color: value > 0 ? "#1A1A1A" : "#9CA3AF" }}
            data-testid={`${testid}-input`}
            aria-label={`${label} interest rate`}
          />
          <span className="font-heading font-black text-base" style={{ color: value > 0 ? "#1A1A1A" : "#9CA3AF" }}>%</span>
        </div>
      </div>
      <input
        type="range"
        min={0} max={30} step={0.1}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        disabled={disabled}
        className="w-full cursor-pointer disabled:cursor-not-allowed"
        style={{ accentColor: accent }}
        data-testid={testid}
      />
      <div className="flex items-center justify-between mt-1 text-[9px] text-gray-400 font-mono">
        <span>0%</span><span>10%</span><span>20%</span><span>30%</span>
      </div>
      {disabled && <div className="text-[9px] text-gray-500 mt-1 italic">Move this slider (or edit the box) to switch from the other model.</div>}
    </div>
  );
}

function ResultCard({ variant, title, subtitle, ratePct, principal, months, emi, total, interest }) {
  const active = ratePct > 0;
  const accent = variant === "reducing" ? "#FFCC00" : "#E65C00";
  return (
    <div
      className={`relative bg-[#1A1A1A] text-white shadow-xl transition-all duration-300 ${active ? "scale-100 opacity-100" : "scale-[0.98] opacity-60"}`}
      data-testid={`emi-${variant}-card`}
    >
      {/* Accent bar */}
      <div className="h-1 w-full" style={{ background: accent }} />
      <div className="px-5 sm:px-6 py-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-black" style={{ color: accent }}>{title}</div>
            <div className="text-[10px] text-gray-400 mt-0.5">{subtitle}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400">Rate</div>
            <div className="font-heading font-black text-lg" style={{ color: accent }}>{ratePct.toFixed(1)}%</div>
          </div>
        </div>

        <div className="mt-5 pb-4 border-b border-white/10">
          <div className="text-[10px] uppercase tracking-[0.2em] text-gray-400">Monthly EMI</div>
          <div className="font-heading font-black text-3xl sm:text-4xl mt-1" style={{ color: accent }} data-testid={`emi-${variant}-monthly`}>
            {formatINR(Math.round(emi))}
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-y-2 gap-x-4 text-[12px]">
          <Stat label="Loan Amount" value={formatINR(Math.round(principal))} testid={`emi-${variant}-principal`} />
          <Stat label="Tenure" value={`${months} mo`} testid={`emi-${variant}-tenure`} />
          <Stat label="Total Interest" value={formatINR(Math.round(interest))} testid={`emi-${variant}-interest`} />
          <Stat label="Total Payable" value={formatINR(Math.round(total))} bold testid={`emi-${variant}-total`} />
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, bold = false, testid }) {
  return (
    <div className="flex flex-col">
      <span className="text-gray-400 text-[10px] uppercase tracking-[0.15em]">{label}</span>
      <span className={`mt-0.5 ${bold ? "font-heading font-black text-base text-white" : "font-mono text-white"}`} data-testid={testid}>{value}</span>
    </div>
  );
}
